import { useCallback, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import {
    BalanceSheetAccountClass,
    toBalanceSheetAccountClass,
} from "@common/constants/domains/accounting/accounts";
import {
    batchUpdateBalanceSheetExtractionRows,
    updateBalanceSheetExtractionDetails,
} from "@api/financialDocument.api";
import { AccountProxyObject } from "./types";

interface RowSnapshot {
    name: string;
    balance: number | null;
    classification: BalanceSheetAccountClass | null;
}

function toRowSnapshot(account: AccountProxyObject): RowSnapshot {
    return {
        name: account.name,
        balance: account.balance,
        classification: toBalanceSheetAccountClass(account.accountClass),
    };
}

function isRowEqual(a: RowSnapshot, b: RowSnapshot): boolean {
    return (
        a.name === b.name &&
        a.balance === b.balance &&
        a.classification === b.classification
    );
}

function hasContent(account: AccountProxyObject): boolean {
    return !!account.name?.trim();
}

interface UseBalanceSheetRowSyncParams {
    financialDocumentId: number | null;
    onSyncError?: (error: Error) => void;
}

/**
 * Immediate per-commit persistence of balance sheet extraction rows.
 * Matches the transactions importer pattern: each grid commit fires a single
 * batch mutation with all changed/deleted rows. One useMutation call per
 * afterChange invocation — no loop, no concurrent mutations.
 */
export function useBalanceSheetRowSync({
    financialDocumentId,
    onSyncError,
}: UseBalanceSheetRowSyncParams) {
    const lastKnownRows = useRef<Map<number, RowSnapshot>>(new Map());
    const lastKnownDate = useRef<string | null>(null);

    const { mutate: batchMutate } = useMutation({
        mutationFn: batchUpdateBalanceSheetExtractionRows,
        onMutate: ({
            upserts,
            deletes,
        }: {
            financialDocumentId: number;
            upserts: Array<RowSnapshot & { rowIndex: number }>;
            deletes: number[];
        }) => {
            const previousRows = new Map(lastKnownRows.current);

            for (const upsert of upserts) {
                lastKnownRows.current.set(upsert.rowIndex, {
                    name: upsert.name,
                    balance: upsert.balance,
                    classification: upsert.classification,
                });
            }

            for (const rowIndex of deletes) {
                lastKnownRows.current.delete(rowIndex);
            }

            return previousRows;
        },
        onError: (error, _, previousRows) => {
            if (previousRows) {
                lastKnownRows.current = previousRows;
            }
            onSyncError?.(
                error instanceof Error
                    ? error
                    : new Error("Failed to save balance sheet rows"),
            );
        },
    });

    const { mutate: detailsMutate } = useMutation({
        mutationFn: updateBalanceSheetExtractionDetails,
        onMutate: ({
            date,
        }: {
            financialDocumentId: number;
            date: string | null;
        }) => {
            const previousDate = lastKnownDate.current;
            lastKnownDate.current = date;
            return previousDate;
        },
        onError: (error, _, previousDate) => {
            if (previousDate !== undefined) {
                lastKnownDate.current = previousDate;
            }
            onSyncError?.(
                error instanceof Error
                    ? error
                    : new Error("Failed to save balance sheet date"),
            );
        },
    });

    const afterChange = useCallback(
        (accounts: AccountProxyObject[]) => {
            if (!financialDocumentId) {
                return;
            }

            const previous = lastKnownRows.current;
            const next = new Map<number, RowSnapshot>();

            for (let rowIndex = 0; rowIndex < accounts.length; rowIndex++) {
                const account = accounts[rowIndex];
                if (hasContent(account)) {
                    next.set(rowIndex, toRowSnapshot(account));
                }
            }

            const upserts: Array<RowSnapshot & { rowIndex: number }> = [];
            const deletes: number[] = [];

            for (const [rowIndex, snapshot] of next) {
                const prev = previous.get(rowIndex);
                if (!prev || !isRowEqual(prev, snapshot)) {
                    upserts.push({ rowIndex, ...snapshot });
                }
            }

            for (const rowIndex of previous.keys()) {
                if (!next.has(rowIndex)) {
                    deletes.push(rowIndex);
                }
            }

            if (upserts.length === 0 && deletes.length === 0) {
                return;
            }

            batchMutate({
                financialDocumentId,
                upserts,
                deletes,
            });
        },
        [financialDocumentId, batchMutate],
    );

    const syncDate = useCallback(
        (date: string | null) => {
            if (!financialDocumentId || date === lastKnownDate.current) {
                return;
            }
            detailsMutate({
                financialDocumentId,
                date,
            });
        },
        [financialDocumentId, detailsMutate],
    );

    const initializeSnapshot = useCallback(
        (initialAccounts: AccountProxyObject[], initialDate: string | null) => {
            const rows = new Map<number, RowSnapshot>();
            for (let i = 0; i < initialAccounts.length; i++) {
                if (hasContent(initialAccounts[i])) {
                    rows.set(i, toRowSnapshot(initialAccounts[i]));
                }
            }
            lastKnownRows.current = rows;
            lastKnownDate.current = initialDate;
        },
        [],
    );

    return { afterChange, syncDate, initializeSnapshot };
}
